import { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  ClipboardPaste,
  Eye,
  FileCode2,
  Loader2,
  MessageSquare,
  Search,
  Send,
  Sparkles,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

// ── Types ────────────────────────────────────────────────────────────────

type Recipient = { email: string; name: string | null; orgs: string[] };
type SelectedRecipient = { name?: string; email: string };
type ChatMessage = { role: "user" | "assistant"; content: string };

// ── Helpers ──────────────────────────────────────────────────────────────

type AuctionContext = {
  id: string;
  title: string;
  ref: string;
  status: string;
  type: string;
  vehicle: { make: string; model: string; variant: string | null; year: number | null };
  imageUrl: string | null;
};

const STATIC_PROMPTS = [
  "Inviter forhandlere til å registrere seg – gratis plattform, lynauksjoner på 15 min",
  "Annonser Tesla Model 3-konkurransen – den som omsetter flest biler vinner",
  "Velkommen til nye forhandlere som nettopp har registrert seg",
];

function buildSuggestedPrompts(auctions: AuctionContext[]) {
  const prompts = [...STATIC_PROMPTS];

  const liveAuctions = auctions.filter((a) => a.status === "live");
  if (liveAuctions.length > 0) {
    const names = liveAuctions.slice(0, 3).map((a) => a.title).join(", ");
    prompts.unshift(`Send ut e-post om dagens aktive auksjoner: ${names}. Inkluder bilder og lenke til plattformen`);
  }

  if (auctions.length > 0) {
    const featured = auctions[0];
    prompts.unshift(`Fremhev ${featured.title} som er på auksjon nå – inkluder bilde, detaljer og CTA for å legge inn bud`);
  }

  return prompts.slice(0, 5);
}

const defaultPreviewText = "Hei {{firstName}}, dette er en forhåndsvisning av meldingen din.";

function renderMarketingTemplate(html: string, recipient: SelectedRecipient | null) {
  if (!recipient) return html;
  const trimmedName = recipient.name?.trim();
  const [firstName = "der"] = trimmedName ? trimmedName.split(/\s+/) : [];
  const fullName = trimmedName || "der";
  return html
    .replaceAll("{{name}}", fullName)
    .replaceAll("{{firstName}}", firstName)
    .replaceAll("{{email}}", recipient.email);
}

const emailLayout = (content: string, previewText?: string) => `
<!DOCTYPE html>
<html lang="nb">
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Forhandlerbørsen</title>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Outfit:wght@700;800;900&display=swap" rel="stylesheet">
    <style>
        body { margin: 0; padding: 0; background-color: #F7F3EB; font-family: 'Inter', system-ui, sans-serif; -webkit-font-smoothing: antialiased; }
        .wrapper { width: 100%; table-layout: fixed; background-color: #F7F3EB; padding-bottom: 40px; }
        .main { background-color: #ffffff; margin: 0 auto; width: 100%; max-width: 600px; border-spacing: 0; color: #001F1E; }
        .header { background-color: #001F1E; padding: 40px 0; text-align: center; }
        .content { padding: 40px 30px; line-height: 1.6; }
        .footer { padding: 30px; text-align: center; color: #64748B; font-size: 13px; }
        .button { background-color: #FF5A1F; color: #ffffff !important; padding: 16px 32px; border-radius: 50px; text-decoration: none; font-weight: 700; display: inline-block; box-shadow: 0 10px 20px rgba(255, 90, 31, 0.2); }
        h1, h2 { font-family: 'Outfit', sans-serif; color: #001F1E; margin-top: 0; letter-spacing: -0.02em; }
        p { margin-bottom: 20px; font-size: 16px; font-weight: 500; }
        .preview { display: none; font-size: 0; color: transparent; line-height: 1px; max-height: 0px; max-width: 0px; opacity: 0; overflow: hidden; }
    </style>
</head>
<body>
    ${previewText ? `<div class="preview">${previewText}</div>` : ""}
    <div class="wrapper">
        <center>
            <table class="main" role="presentation">
                <tr>
                    <td class="header">
                        <img src="https://forhandlerborsen.no/branding/logo-dark.svg" alt="Forhandlerbørsen" width="200" style="display: block; margin: 0 auto;">
                    </td>
                </tr>
                <tr>
                    <td class="content">
                        ${content}
                    </td>
                </tr>
                <tr>
                    <td class="footer">
                        <strong>Forhandlerbørsen</strong><br>
                        Norges mest effektive B2B markedsplass for bilhandel<br><br>
                        &copy; ${new Date().getFullYear()} Forhandlerbørsen. Alle rettigheter reservert.
                    </td>
                </tr>
            </table>
        </center>
    </div>
</body>
</html>
`;

async function streamEmailGeneration(
  messages: ChatMessage[],
  currentHtml: string | undefined,
  currentSubject: string | undefined,
  onDelta: (text: string) => void,
  onDone: (html: string, subject?: string) => void,
  onError: (error: string) => void,
  signal?: AbortSignal,
) {
  const response = await fetch(`${API}/api/admin/marketing/generate-email`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, currentHtml, currentSubject }),
    signal,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || "Kunne ikke generere e-post");
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("Ingen respons-strøm");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = JSON.parse(line.slice(6)) as
        | { type: "delta"; text: string }
        | { type: "done"; html: string; subject?: string }
        | { type: "error"; error: string };

      if (data.type === "delta") onDelta(data.text);
      else if (data.type === "done") onDone(data.html, data.subject);
      else if (data.type === "error") onError(data.error);
    }
  }
}

type RecipientMode = "search" | "paste" | "platform";

function parseRecipientInput(input: string) {
  const lines = input.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const recipients: SelectedRecipient[] = [];
  const invalidRows: string[] = [];
  const seen = new Set<string>();

  for (const line of lines) {
    const parts = line.split(/\t|;|,/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) { invalidRows.push(line); continue; }

    const [firstPart, secondPart] = parts;
    const emailCandidate = parts.length === 1 ? firstPart : secondPart;
    const name = parts.length === 1 ? undefined : firstPart;
    const email = emailCandidate.toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { invalidRows.push(line); continue; }
    if (seen.has(email)) continue;

    seen.add(email);
    recipients.push({ email, name });
  }

  return { recipients, invalidRows };
}

// ── Persistence ──────────────────────────────────────────────────────────

const AI_DRAFT_KEY = "marketing-ai-draft";

type AiDraft = {
  subject?: string;
  html?: string;
  chatMessages?: ChatMessage[];
  selectedRecipients?: SelectedRecipient[];
};

function loadAiDraft(): AiDraft | null {
  try {
    const raw = sessionStorage.getItem(AI_DRAFT_KEY);
    return raw ? JSON.parse(raw) as AiDraft : null;
  } catch { return null; }
}

// ── Component ────────────────────────────────────────────────────────────

export function MarketingAiPage() {
  const draft = useRef(loadAiDraft());

  // Recipients
  const [allRecipients, setAllRecipients] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<SelectedRecipient[]>(draft.current?.selectedRecipients ?? []);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(true);
  const [recipientMode, setRecipientMode] = useState<RecipientMode>("search");
  const [pasteInput, setPasteInput] = useState("");

  // Email
  const [subject, setSubject] = useState(draft.current?.subject ?? "Invitasjon fra Forhandlerbørsen");
  const [html, setHtml] = useState(draft.current?.html ?? "");
  const [activeTab, setActiveTab] = useState("ai");

  // AI chat
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(draft.current?.chatMessages ?? []);
  const [chatInput, setChatInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingHtml, setStreamingHtml] = useState("");
  const [aiError, setAiError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Send
  const [isSending, setIsSending] = useState(false);
  const [sendMessage, setSendMessage] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // ── Persist draft ────────────────────────────────────────────────────

  const saveDraft = useCallback(() => {
    sessionStorage.setItem(AI_DRAFT_KEY, JSON.stringify({ subject, html, chatMessages, selectedRecipients }));
  }, [subject, html, chatMessages, selectedRecipients]);

  useEffect(() => { saveDraft(); }, [saveDraft]);

  // Auction context for suggested prompts
  const [auctionContext, setAuctionContext] = useState<AuctionContext[]>([]);

  // ── Load recipients + auctions ──────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/marketing/recipients`, { credentials: "include" });
        if (res.ok) setAllRecipients(await res.json() as Recipient[]);
      } catch { /* ignore */ }
      finally { setIsLoadingRecipients(false); }
    })();
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/marketing/auctions-context`, { credentials: "include" });
        if (res.ok) setAuctionContext(await res.json() as AuctionContext[]);
      } catch { /* ignore */ }
    })();
  }, []);

  const suggestedPrompts = buildSuggestedPrompts(auctionContext);

  // ── AI generation ───────────────────────────────────────────────────

  const handleGenerate = async (prompt?: string) => {
    const userMessage = prompt ?? chatInput.trim();
    if (!userMessage || isGenerating) return;

    setChatInput("");
    setAiError(null);
    setIsGenerating(true);
    setStreamingHtml("");

    const newMessages: ChatMessage[] = [...chatMessages, { role: "user", content: userMessage }];
    setChatMessages(newMessages);

    const controller = new AbortController();
    abortRef.current = controller;
    let accumulated = "";

    try {
      await streamEmailGeneration(
        newMessages,
        html.trim() && chatMessages.length > 0 ? html : undefined,
        subject.trim() && chatMessages.length > 0 ? subject : undefined,
        (delta) => { accumulated += delta; setStreamingHtml(accumulated); },
        (finalHtml, suggestedSubject) => {
          setHtml(finalHtml);
          if (suggestedSubject) setSubject(suggestedSubject);
          setStreamingHtml("");
          setChatMessages((prev) => [...prev, { role: "assistant", content: "E-post generert." }]);
        },
        (error) => { setAiError(error); setStreamingHtml(""); },
        controller.signal,
      );
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        setAiError(error instanceof Error ? error.message : "Ukjent feil");
      }
      setStreamingHtml("");
    } finally {
      setIsGenerating(false);
      abortRef.current = null;
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, isGenerating]);

  // ── Recipient helpers ───────────────────────────────────────────────

  const filteredRecipients = allRecipients.filter((r) => {
    const q = recipientSearch.toLowerCase();
    if (!q) return true;
    return (
      r.email.toLowerCase().includes(q) ||
      (r.name?.toLowerCase().includes(q) ?? false) ||
      r.orgs.some((o) => o.toLowerCase().includes(q))
    );
  });

  const selectedEmails = new Set(selectedRecipients.map((r) => r.email));

  const toggleRecipient = (r: Recipient) => {
    if (selectedEmails.has(r.email)) {
      setSelectedRecipients((prev) => prev.filter((s) => s.email !== r.email));
    } else {
      setSelectedRecipients((prev) => [...prev, { email: r.email, name: r.name ?? undefined }]);
    }
  };

  const selectAll = () => {
    const toAdd = filteredRecipients
      .filter((r) => !selectedEmails.has(r.email))
      .map((r) => ({ email: r.email, name: r.name ?? undefined }));
    setSelectedRecipients((prev) => [...prev, ...toAdd]);
  };

  const deselectAll = () => {
    const filteredEmails = new Set(filteredRecipients.map((r) => r.email));
    setSelectedRecipients((prev) => prev.filter((s) => !filteredEmails.has(s.email)));
  };

  // Platform-only = recipients that belong to at least one org
  const platformRecipients = allRecipients.filter((r) => r.orgs.length > 0);

  const selectAllPlatform = () => {
    const existing = new Set(selectedRecipients.map((r) => r.email));
    const toAdd = platformRecipients
      .filter((r) => !existing.has(r.email))
      .map((r) => ({ email: r.email, name: r.name ?? undefined }));
    setSelectedRecipients((prev) => [...prev, ...toAdd]);
  };

  // Paste parsing
  const parsedPaste = parseRecipientInput(pasteInput);

  const applyPaste = () => {
    const existing = new Set(selectedRecipients.map((r) => r.email));
    const toAdd = parsedPaste.recipients.filter((r) => !existing.has(r.email));
    setSelectedRecipients((prev) => [...prev, ...toAdd]);
    setPasteInput("");
  };

  // ── Send ────────────────────────────────────────────────────────────

  const previewRecipient = selectedRecipients[0] ?? null;

  const handleSend = async () => {
    setSendMessage(null);
    setSendError(null);

    if (!subject.trim()) { setSendError("Legg inn et emne."); return; }
    if (!html.trim()) { setSendError("Generer en e-post først."); return; }
    if (!selectedRecipients.length) { setSendError("Velg minst én mottaker."); return; }

    setIsSending(true);
    try {
      const response = await fetch(`${API}/api/admin/marketing/send-email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: subject.trim(),
          html,
          recipients: selectedRecipients,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { queued?: number; failed?: number; error?: string }
        | null;

      if (!response.ok) throw new Error(payload?.error || "Kunne ikke sende.");

      setSendMessage(
        payload?.failed
          ? `${payload?.queued ?? selectedRecipients.length} i kø. ${payload.failed} feilet.`
          : `${payload?.queued ?? selectedRecipients.length} e-poster lagt i kø.`,
      );
      setSelectedRecipients([]);
    } catch (error) {
      setSendError(error instanceof Error ? error.message : "Ukjent feil.");
    } finally {
      setIsSending(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────

  const rawDisplayHtml = streamingHtml || html;
  // Strip SUBJECT: line from streaming preview (backend strips it in final response)
  const displayHtml = rawDisplayHtml.replace(/^SUBJECT:\s*.+\n\n?/, "");
  const previewHtml = displayHtml
    ? emailLayout(
        renderMarketingTemplate(displayHtml, previewRecipient),
        renderMarketingTemplate(defaultPreviewText, previewRecipient),
      )
    : "";

  return (
    <div className="mx-auto flex max-w-screen-2xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="rounded-[28px] border border-border bg-[linear-gradient(135deg,#fffaf1_0%,#f4efe5_100%)] p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <Badge variant="accent">AI Marketing</Badge>
            <div>
              <h1
                className="text-3xl font-black tracking-tight text-foreground"
                style={{ fontFamily: "var(--font-display)" }}
              >
                AI e-postbygger
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                Beskriv e-posten du vil sende, velg mottakere, og send. AI-en lager innholdet for deg.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-black/5 bg-white/70 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Valgte mottakere</p>
              <p className="mt-1 text-2xl font-black tracking-tight text-foreground">{selectedRecipients.length}</p>
            </div>
            <div className="rounded-2xl border border-black/5 bg-white/70 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Tilgjengelige</p>
              <p className="mt-1 text-2xl font-black tracking-tight text-foreground">
                {isLoadingRecipients ? "..." : allRecipients.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content: 3-column on large screens */}
      <div className="grid gap-4 xl:grid-cols-[300px_1fr]">
        {/* Left: Recipients */}
        <Card className="xl:row-span-2">
          <CardHeader className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Mottakere</CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">
                  {selectedRecipients.length > 0
                    ? `${selectedRecipients.length} valgt`
                    : "Velg hvem som skal motta e-posten"}
                </p>
              </div>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>

            {/* Mode switcher */}
            <div className="flex gap-1 rounded-xl bg-muted/50 p-1">
              {([
                { mode: "search" as const, icon: Search, label: "Søk" },
                { mode: "paste" as const, icon: ClipboardPaste, label: "Lim inn" },
                { mode: "platform" as const, icon: UserCheck, label: "Plattform" },
              ]).map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => setRecipientMode(mode)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition ${
                    recipientMode === mode
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </CardHeader>

          <CardContent className="space-y-3">
            {/* Selected chips (always visible) */}
            {selectedRecipients.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {selectedRecipients.slice(0, 8).map((r) => (
                    <span
                      key={r.email}
                      className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
                    >
                      {r.name?.split(" ")[0] || r.email.split("@")[0]}
                      <button
                        onClick={() => setSelectedRecipients((prev) => prev.filter((s) => s.email !== r.email))}
                        className="hover:text-accent/70"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {selectedRecipients.length > 8 && (
                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                      +{selectedRecipients.length - 8} til
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setSelectedRecipients([])}
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                >
                  Fjern alle
                </button>
              </div>
            )}

            {/* ── Search mode ── */}
            {recipientMode === "search" && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    placeholder="Søk navn, e-post, org..."
                    className="w-full rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{filteredRecipients.length} treff</span>
                  <button onClick={selectAll} className="text-accent hover:underline">
                    Velg alle
                  </button>
                </div>

                <div className="max-h-[calc(100vh-560px)] min-h-[250px] space-y-1 overflow-y-auto">
                  {isLoadingRecipients ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredRecipients.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">Ingen treff</p>
                  ) : (
                    filteredRecipients.map((r) => {
                      const isSelected = selectedEmails.has(r.email);
                      return (
                        <button
                          key={r.email}
                          onClick={() => toggleRecipient(r)}
                          className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                            isSelected ? "bg-accent/5 ring-1 ring-accent/20" : "hover:bg-muted/50"
                          }`}
                        >
                          <div
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                              isSelected ? "border-accent bg-accent text-white" : "border-border bg-background"
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-foreground">
                              {r.name || r.email}
                            </p>
                            {r.name && <p className="truncate text-xs text-muted-foreground">{r.email}</p>}
                            {r.orgs.length > 0 && (
                              <p className="mt-0.5 truncate text-xs text-muted-foreground">{r.orgs.join(", ")}</p>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {/* ── Paste mode ── */}
            {recipientMode === "paste" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Lim inn fra Excel: <code className="rounded bg-muted px-1">E-post</code>, <code className="rounded bg-muted px-1">Navn[TAB]E-post</code> eller <code className="rounded bg-muted px-1">Navn, E-post</code>
                </p>
                <textarea
                  value={pasteInput}
                  onChange={(e) => setPasteInput(e.target.value)}
                  placeholder={`ola@example.no\nKari Nordmann\tkari@example.no`}
                  className="min-h-[200px] w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm leading-6 outline-none transition focus-visible:ring-2 focus-visible:ring-ring"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    {parsedPaste.recipients.length > 0 && (
                      <span className="text-foreground font-medium">{parsedPaste.recipients.length} gyldige</span>
                    )}
                    {parsedPaste.invalidRows.length > 0 && (
                      <span className="text-amber-600 ml-2">{parsedPaste.invalidRows.length} ugyldige</span>
                    )}
                  </div>
                  <Button
                    onClick={applyPaste}
                    disabled={parsedPaste.recipients.length === 0}
                    className="gap-1.5 h-9 px-4 text-sm"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Legg til {parsedPaste.recipients.length}
                  </Button>
                </div>
                {parsedPaste.invalidRows.length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-medium text-amber-800">Kunne ikke tolkes:</p>
                    <div className="mt-1 max-h-24 space-y-0.5 overflow-y-auto text-xs text-amber-700">
                      {parsedPaste.invalidRows.map((row) => <p key={row}>{row}</p>)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Platform mode ── */}
            {recipientMode === "platform" && (
              <div className="space-y-3">
                <div className="rounded-2xl border border-border bg-muted/20 p-4 text-center">
                  <UserCheck className="mx-auto h-8 w-8 text-accent/60" />
                  <p className="mt-3 text-sm font-semibold text-foreground">Registrerte forhandlere</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {isLoadingRecipients
                      ? "Laster..."
                      : `${platformRecipients.length} brukere fra ${new Set(platformRecipients.flatMap((r) => r.orgs)).size} organisasjoner`}
                  </p>
                  <Button onClick={selectAllPlatform} className="mt-4 gap-2 h-9 px-4 text-sm">
                    <Users className="h-3.5 w-3.5" />
                    Velg alle {platformRecipients.length} plattformbrukere
                  </Button>
                </div>

                {/* Quick org filter */}
                {!isLoadingRecipients && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Eller velg per organisasjon:</p>
                    <div className="max-h-[calc(100vh-640px)] min-h-[150px] space-y-1 overflow-y-auto">
                      {[...new Set(platformRecipients.flatMap((r) => r.orgs))].sort((a, b) => a.localeCompare(b, "nb")).map((org) => {
                        const orgMembers = platformRecipients.filter((r) => r.orgs.includes(org));
                        const allSelected = orgMembers.every((r) => selectedEmails.has(r.email));
                        return (
                          <button
                            key={org}
                            onClick={() => {
                              if (allSelected) {
                                const orgEmails = new Set(orgMembers.map((r) => r.email));
                                setSelectedRecipients((prev) => prev.filter((s) => !orgEmails.has(s.email)));
                              } else {
                                const existing = new Set(selectedRecipients.map((r) => r.email));
                                const toAdd = orgMembers
                                  .filter((r) => !existing.has(r.email))
                                  .map((r) => ({ email: r.email, name: r.name ?? undefined }));
                                setSelectedRecipients((prev) => [...prev, ...toAdd]);
                              }
                            }}
                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                              allSelected ? "bg-accent/5 ring-1 ring-accent/20" : "hover:bg-muted/50"
                            }`}
                          >
                            <div
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                                allSelected ? "border-accent bg-accent text-white" : "border-border bg-background"
                              }`}
                            >
                              {allSelected && <Check className="h-3 w-3" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-foreground">{org}</p>
                              <p className="text-xs text-muted-foreground">{orgMembers.length} medlemmer</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Email builder */}
        <Card>
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">E-post</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Beskriv e-posten, juster med oppfølgingsmeldinger, og send.
                </p>
              </div>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="grid gap-3">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Emne
                </label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Skriv e-postemne"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Merge tags:</span>
                <Badge variant="muted">{"{{name}}"}</Badge>
                <Badge variant="muted">{"{{firstName}}"}</Badge>
                <Badge variant="muted">{"{{email}}"}</Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="ai" className="gap-2">
                  <Sparkles className="h-4 w-4" />
                  AI Builder
                </TabsTrigger>
                <TabsTrigger value="html" className="gap-2">
                  <FileCode2 className="h-4 w-4" />
                  HTML
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-2">
                  <Eye className="h-4 w-4" />
                  Preview
                </TabsTrigger>
              </TabsList>

              {/* AI Builder tab */}
              <TabsContent value="ai">
                <div className="grid gap-4 lg:grid-cols-2" style={{ minHeight: 600 }}>
                  {/* Chat */}
                  <div className="flex flex-col rounded-2xl border border-border bg-muted/20">
                    <div className="flex-1 space-y-3 overflow-y-auto p-4" style={{ maxHeight: 480 }}>
                      {chatMessages.length === 0 && !isGenerating ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-6 text-center">
                          <div className="rounded-2xl bg-accent/10 p-4">
                            <Sparkles className="h-8 w-8 text-accent" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              Beskriv e-posten du vil lage
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              AI-en lager innholdet. Du kan justere etterpå.
                            </p>
                          </div>
                          <div className="grid w-full gap-2">
                            {suggestedPrompts.map((prompt) => (
                              <button
                                key={prompt}
                                onClick={() => void handleGenerate(prompt)}
                                className="rounded-xl border border-border bg-background px-3 py-2.5 text-left text-xs leading-5 text-foreground transition hover:border-accent/40 hover:bg-accent/5"
                              >
                                {prompt}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((msg, i) => (
                            <div
                              key={i}
                              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-6 ${
                                  msg.role === "user"
                                    ? "bg-[#001F1E] text-white"
                                    : "border border-border bg-background text-foreground"
                                }`}
                              >
                                {msg.role === "assistant" ? (
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-accent" />
                                    <span>{msg.content}</span>
                                  </div>
                                ) : (
                                  msg.content
                                )}
                              </div>
                            </div>
                          ))}
                          {isGenerating && (
                            <div className="flex justify-start">
                              <div className="flex items-center gap-2 rounded-2xl border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
                                Genererer e-post...
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                        </>
                      )}
                    </div>

                    {aiError && (
                      <div className="mx-4 mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {aiError}
                      </div>
                    )}

                    <div className="border-t border-border p-3">
                      <div className="flex gap-2">
                        <input
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              void handleGenerate();
                            }
                          }}
                          placeholder={
                            chatMessages.length === 0
                              ? "Beskriv e-posten du vil lage..."
                              : "Juster e-posten..."
                          }
                          disabled={isGenerating}
                          className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                        />
                        <Button
                          onClick={() => void handleGenerate()}
                          disabled={isGenerating || !chatInput.trim()}
                          size="sm"
                          className="gap-1.5 rounded-xl"
                        >
                          {isGenerating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MessageSquare className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {chatMessages.length > 0 && (
                        <button
                          onClick={() => {
                            abortRef.current?.abort();
                            setChatMessages([]);
                            setStreamingHtml("");
                            setAiError(null);
                            setHtml("");
                          }}
                          className="mt-2 text-xs text-muted-foreground underline hover:text-foreground"
                        >
                          Start på nytt
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Live preview */}
                  <div className="overflow-hidden rounded-2xl border border-border bg-white">
                    {displayHtml ? (
                      <iframe
                        title="AI email preview"
                        srcDoc={previewHtml}
                        className="h-full w-full bg-white"
                        style={{ minHeight: 600 }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
                        Forhåndsvisning vises her når du har generert innhold.
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* HTML tab */}
              <TabsContent value="html">
                <textarea
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  className="w-full resize-y rounded-2xl border border-input bg-[#fffdf8] px-4 py-3 font-mono text-[13px] leading-6 outline-none transition focus-visible:ring-2 focus-visible:ring-ring"
                  style={{ minHeight: 600, height: 600 }}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Rediger HTML direkte. Logo, ramme og footer legges på automatisk.
                </p>
              </TabsContent>

              {/* Preview tab */}
              <TabsContent value="preview">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-muted/35 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{subject || "Uten emne"}</p>
                      <p className="text-xs text-muted-foreground">
                        Preview for{" "}
                        {previewRecipient
                          ? previewRecipient.name
                            ? `${previewRecipient.name} <${previewRecipient.email}>`
                            : previewRecipient.email
                          : "første valgte mottaker"}
                      </p>
                    </div>
                    <Badge variant={previewRecipient ? "accent" : "muted"}>
                      {previewRecipient ? "Personalisert" : "Standard"}
                    </Badge>
                  </div>

                  <div className="overflow-hidden rounded-[24px] border border-border bg-white">
                    <iframe
                      title="Marketing email preview"
                      srcDoc={previewHtml || emailLayout("<p style='color:#64748B;'>Ingen innhold ennå.</p>")}
                      className="w-full bg-white"
                      style={{ minHeight: 600, height: 600 }}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Send bar */}
            <div className="mt-5 flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                {sendMessage && <p className="text-sm font-medium text-emerald-700">{sendMessage}</p>}
                {sendError && <p className="text-sm font-medium text-destructive">{sendError}</p>}
                <p className="text-xs text-muted-foreground">
                  Sender én e-post per mottaker. Mottakerne ser ikke hverandre.
                </p>
              </div>

              <Button
                onClick={() => void handleSend()}
                disabled={isSending || !selectedRecipients.length || !html.trim()}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {isSending ? "Sender..." : `Send til ${selectedRecipients.length}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
